import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { CurrentUnits } from "@/libs/units/current";
import { GenericUnits } from "@/libs/units/generic";
import { PercentageUnits } from "@/libs/units/percentage";
import { PowerUnits } from "@/libs/units/power";
import { VoltageUnits } from "@/libs/units/voltage";
import { EquipmentCalculatorSectionKey } from "../../calculationTypes";
import type { ConverterInputProps } from "@/components/types";
import type { Section, SectionDeps } from "../types";
import { ScenarioDetailSectionKey, ScenarioDetailSectionTitle } from "../types";

export const buildSurfaceDriveSideCalculatorSection = ({
  scenarioCalculations,
  getRealPowerUnit,
  getCurrentUnit
}: SectionDeps): Section => ({
  id: EquipmentCalculatorSectionKey.SURFACE_DRIVE,
  hash: ScenarioDetailSectionKey.SURFACE_DRIVE,
  label: ScenarioDetailSectionTitle.SURFACE_DRIVE,
  routeName: "SizingParameters",
  aggregateDisplays: [
    {
      label: "Required Voltage",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      value:
        scenarioCalculations.surfaceDriveCalculations.requiredVoltageV.value,
      mode: "convert"
    },
    {
      label: "Required kVA",
      base: ApparentPowerUnits.KiloVoltAmps,
      model: ApparentPowerUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.requiredKVA.value,
      mode: "convert"
    },
    {
      label: "System Power",
      base: PowerUnits.KiloWatts,
      target: getRealPowerUnit().value,
      model: PowerUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.systemPowerKW.value,
      mode: "convert"
    },
    {
      label: "Surface Power Factor",
      base: PercentageUnits.Ratio,
      model: PercentageUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.surfacePFRatio.value,
      mode: "read"
    },
    ...(scenarioCalculations.isScenarioEspShore.value
      ? [
          {
            label: "Total System Efficiency",
            base: PercentageUnits.Ratio,
            target: PercentageUnits.Percentage,
            model: PercentageUnits.options(),
            value:
              scenarioCalculations.surfaceDriveCalculations
                .totalSystemEfficiencyRatio.value,
            mode: "read"
          } as ConverterInputProps,
          {
            label: "Monthly System Power Cost",
            base: GenericUnits.UnitedStatesDollar,
            model: [GenericUnits.UnitedStatesDollar],
            value:
              scenarioCalculations.surfaceDriveCalculations.monthlySystemCostUSD
                .value,
            mode: "read"
          } as ConverterInputProps
        ]
      : []),
    {
      label: "Required Voltage @ Full Load",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      value:
        scenarioCalculations.surfaceDriveCalculations.requiredVoltageAtMaxHzV
          .value,
      mode: "convert"
    },
    {
      label: "Transformer Tap Voltage",
      base: VoltageUnits.Volts,
      model: VoltageUnits.options(),
      value:
        scenarioCalculations.surfaceDriveCalculations.transformerTapData.value
          .availableVoltage,
      mode: "convert"
    },
    {
      label: "I Limit",
      base: CurrentUnits.Amperes,
      target: getCurrentUnit().value,
      model: CurrentUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.iLimitA.value,
      mode: "read"
    },
    {
      label: "Underload",
      base: CurrentUnits.Amperes,
      target: getCurrentUnit().value,
      model: CurrentUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.vsdUnderloadA.value,
      mode: "read"
    },
    {
      label: "Overload",
      base: CurrentUnits.Amperes,
      target: getCurrentUnit().value,
      model: CurrentUnits.options(),
      value: scenarioCalculations.surfaceDriveCalculations.vsdOverloadA.value,
      mode: "read"
    }
  ] as ConverterInputProps[]
});
